'use client';

import React, { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Box, HStack, SimpleGrid, Stack, Text } from '@chakra-ui/react';
import { useTranslations } from 'next-intl';
import {
  ICartItem,
  IModifierQuantityObj,
  IRedux,
} from '@/_store/types';
import { ISavedModifier, IProductVariant } from '@/_types';
import {
  onDecreaseModifierQuantity,
  onDecreaseModifierVariantQuantity,
  onGroupModifierChange,
  onIncreaseModifierQuantity,
  onIncreaseModifierVariantQuantity,
  onModifierChange,
} from '@/_utils/modifier/actions';
import { getProductModifier } from '@/_service/product.service';
import { useCustomLocale } from '@/_hooks/useCustomLocale';
import { useSelector } from 'react-redux';
import { usePathname } from '@/i18n/navigation';
import ModifierCheckbox from './checkbox';
import { IProduct } from '@/entities/product/model/types';
import { useActiveBrand } from '@/_hooks/useActiveBrand'

interface IProps {
  product?: IProduct | IProductVariant;
  quantity: number;
  productInCart: ICartItem | null;
  expectedModifiers: string[];
  orderModifiers: ISavedModifier[];
  modifiersQuantity: IModifierQuantityObj[];
  setOrderModifiers: React.Dispatch<React.SetStateAction<ISavedModifier[]>>;
  setModifiersQuantity: React.Dispatch<
    React.SetStateAction<IModifierQuantityObj[]>
  >;
  onSaveCompulsories: (e: any[]) => void;
  scrollToExpectedModifierRef?: any;
}

function Modifiers({
  product,
  quantity,
  productInCart,
  orderModifiers,
  modifiersQuantity,
  expectedModifiers,
  setOrderModifiers,
  setModifiersQuantity,
  onSaveCompulsories,
  scrollToExpectedModifierRef,
}: IProps) {
  const { branches } = useSelector((state: IRedux) => state.common);
  const { is_hall, menu_id } = useSelector((state: IRedux) => state.session);

  const pathname = usePathname();
  const shipperId = useActiveBrand()
  const branch = branches?.[shipperId]

  const isHall = is_hall === 'true';

  const isMenuActive =
    isHall || pathname.endsWith('/menu')
      ? menu_id !== 'null'
        ? menu_id
        : ''
      : branch?.menu_id || '';

  const { data } = useQuery({
    queryKey: ['modifier', product?.id],
    queryFn: () =>
      getProductModifier({
        product_id: product?.id,
        menu_id: isMenuActive,
      }).then((res) => res?.data),
    enabled: Boolean(product?.has_modifier),
  });

  const currentLocale = useCustomLocale();
  const t = useTranslations();

  // Get modifiers by variant
  useEffect(() => {
    if (!data) return;

    const checkIsActive = (activeInMenu: boolean) =>
      (isMenuActive && activeInMenu) || !isMenuActive;

    let modifiers: ISavedModifier[] = [];
    let groupAmounts: { id: string; quantity: number }[] = [];
    let compulsoryModifiers: ISavedModifier[] = [];

    // Handle group modifiers
    data.product_modifiers?.group_modifiers?.forEach((group: any) => {
      let amount = 0;

      group.variants?.forEach((variant: any) => {
        const isActive = checkIsActive(
          group.active_in_menu && variant.active_in_menu,
        );

        const item: ISavedModifier = {
          name: variant.title,
          parent_id: group.id,
          parent_name: group.name,
          parent_min_amount: group.min_amount,
          modifier_id: variant.id,
          modifier_price: +variant.out_price,
          max_amount: variant.max_amount || 1,
          min_amount: variant.min_amount || 0,
          modifier_quantity: isActive
            ? variant.default_amount || variant.min_amount || 0
            : 0,
          type: 'group',
        };

        modifiers.push(item);
        if (isActive) {
          amount += variant.default_amount || variant.min_amount || 0;
        }
        if (group.is_compulsory) {
          compulsoryModifiers.push(item);
        }
      });

      groupAmounts.push({ id: group.id, quantity: amount });
    });

    // Handle single modifiers
    data.product_modifiers?.single_modifiers?.forEach((item: any) => {
      const isActive = checkIsActive(item.active_in_menu);

      const singleModifier: ISavedModifier = {
        modifier_id: item.id,
        modifier_price: +item.price,
        max_amount: item.max_amount || 1,
        min_amount: item.min_amount || 0,
        modifier_quantity: isActive
          ? item.default_amount || item.min_amount || 0
          : 0,
        parent_id: '',
        name: item.name,
        type: 'single',
      };

      modifiers.push(singleModifier);
      if (item.is_compulsory) {
        compulsoryModifiers.push(singleModifier);
      }
    });

    setOrderModifiers(modifiers);
    setModifiersQuantity(groupAmounts);
    onSaveCompulsories(compulsoryModifiers);
  }, [data]);

  const modifierQuantityHandler = (modifierId: string) => {
    const element = orderModifiers.find(
      (item: any) => item.modifier_id === modifierId,
    );

    return element?.modifier_quantity ?? 0;
  };
  const checkModifierHandler = (modifierId: string) => {
    const isChecked = orderModifiers.some(
      (item) => item.modifier_id === modifierId && item.modifier_quantity > 0,
    );
    return isChecked ? isChecked : isOrderedModifierHandler(modifierId);
  };
  const isOrderedModifierHandler = (modifierId: string) => {
    if (
      productInCart?.order_modifiers &&
      productInCart?.order_modifiers?.length > 0
    ) {
      let isIncluded = false;
      productInCart?.order_modifiers?.map((item) => {
        if (item.modifier_id == modifierId && item.modifier_quantity > 0) {
          isIncluded = true;
        }
      });
      return isIncluded;
    }
    return false;
  };

  return (
    <Stack mb={4} gap={2}>
      {data?.product_modifiers?.group_modifiers?.map(
        (modifier, idx) =>
          modifiersQuantity[idx]?.id === modifier.id && (
            <Box key={modifier.id + modifier.from_product_id + idx}>
              <HStack justify='space-between' my={2}>
                <Text
                  fontWeight={600}
                  lineHeight={'14px'}
                  ref={
                    expectedModifiers?.[0] === modifier.name[currentLocale]
                      ? scrollToExpectedModifierRef
                      : null
                  }
                  color={
                    expectedModifiers.includes(modifier.name[currentLocale])
                      ? 'red'
                      : 'text'
                  }
                >
                  {modifier.name?.[currentLocale]}
                </Text>
                <HStack>
                  <Text fontSize='sm' color='text.muted'>
                    {modifier?.min_amount ? (
                      <>
                        {t('at_least').substring(0, 3)}: {modifier?.min_amount}
                      </>
                    ) : (
                      t('optional')
                    )}
                  </Text>
                  <Text fontSize='sm' color='text.muted'>
                    {modifier?.max_amount ? (
                      <>
                        {t('maximum').substring(0, 4)}: {modifier?.max_amount}
                      </>
                    ) : (
                      t('optional')
                    )}
                  </Text>
                </HStack>
              </HStack>
              <SimpleGrid
                columns={{
                  base: modifier?.is_checkbox ? 2 : 1,
                  md: modifier?.is_checkbox ? 3 : 1,
                }}
                gap={3}
              >
                {modifier?.variants?.map((variant, index) => (
                  <Box key={variant.id + index}>
                    <ModifierCheckbox
                      data={variant}
                      inStop={Boolean(
                        isMenuActive &&
                        (!modifier.active_in_menu || !variant.active_in_menu),
                      )}
                      isCheckbox={modifier?.is_checkbox}
                      checked={checkModifierHandler(variant?.id)}
                      quantity={modifierQuantityHandler(variant?.id) * quantity}
                      onChange={({ target }) =>
                        onGroupModifierChange(
                          target.checked,
                          variant,
                          modifier,
                          orderModifiers,
                          setOrderModifiers,
                          modifiersQuantity,
                          setModifiersQuantity,
                        )
                      }
                      label={variant.title?.[currentLocale]}
                      outPrice={variant.out_price}
                      decrease={() =>
                        onDecreaseModifierVariantQuantity(
                          variant,
                          modifier,
                          orderModifiers,
                          setOrderModifiers,
                          modifiersQuantity,
                          setModifiersQuantity,
                        )
                      }
                      increase={() =>
                        onIncreaseModifierVariantQuantity(
                          variant,
                          modifier,
                          orderModifiers,
                          setOrderModifiers,
                          modifiersQuantity,
                          setModifiersQuantity,
                        )
                      }
                    />
                  </Box>
                ))}
              </SimpleGrid>
            </Box>
          ),
      )}
      {data?.product_modifiers?.single_modifiers?.map((modifier, idx) => (
        <Box key={modifier.id + modifier.from_product_id + idx}>
          <HStack justify='space-between' my={2}>
            <Text
              fontWeight={600}
              lineHeight={'14px'}
              color={
                expectedModifiers.includes(modifier.name[currentLocale])
                  ? 'red'
                  : 'text'
              }
            >
              {modifier.name?.[currentLocale]}
            </Text>
            <HStack>
              <Text fontSize='sm' color='text.muted'>
                {modifier?.min_amount ? (
                  <>
                    {t('at_least').substring(0, 3)}: {modifier?.min_amount}
                  </>
                ) : (
                  t('optional')
                )}
              </Text>
              <Text fontSize='sm' color='text.muted'>
                {modifier?.max_amount ? (
                  <>
                    {t('maximum').substring(0, 3)}: {modifier?.max_amount}
                  </>
                ) : (
                  t('optional')
                )}
              </Text>
            </HStack>
          </HStack>
          <SimpleGrid
            columns={{
              base: modifier?.is_checkbox ? 2 : 1,
              md: modifier?.is_checkbox ? 3 : 1,
            }}
            gap={3}
          >
            <ModifierCheckbox
              data={modifier}
              inStop={Boolean(isMenuActive && !modifier.active_in_menu)}
              isCheckbox={modifier?.is_checkbox}
              checked={checkModifierHandler(modifier?.id)}
              quantity={modifierQuantityHandler(modifier?.id) * quantity}
              onChange={({ target }) =>
                onModifierChange(target.checked, modifier, setOrderModifiers)
              }
              label={modifier.name?.[currentLocale]}
              outPrice={modifier.price}
              isCompulsory={modifier.is_compulsory}
              decrease={() =>
                onDecreaseModifierQuantity(
                  modifier,
                  orderModifiers,
                  setOrderModifiers,
                )
              }
              increase={() =>
                onIncreaseModifierQuantity(
                  modifier,
                  orderModifiers,
                  setOrderModifiers,
                )
              }
            />
          </SimpleGrid>
        </Box>
      ))}
    </Stack>
  );
}

export default Modifiers;
